import { isNestedTypeProperty, sortPropertyByTypeElementDeveloperName } from '../../sources/type';
import type { TypeElementResponseAPI, TypeMappingAutoAPI } from '../../types/type';
import './automapping-map.css';
import PropertyDetails from './PropertyDetails';

type Types = Record<string, TypeElementResponseAPI>;

interface Props {
    targetTypeElementId: string;
    types: Types;
    mapping: TypeMappingAutoAPI | null | undefined;
    visitedTypeElementIds?: Set<string>;
}

const hasUnmappedNestedProperty = (
    targetType: TypeElementResponseAPI,
    mapping: TypeMappingAutoAPI | null | undefined,
    types: Types,
    visitedTypeElementIds: Set<string> = new Set<string>(),
): boolean => {
    const nextVisited = new Set(visitedTypeElementIds).add(targetType.id);

    for (const property of targetType.properties) {
        const mappedProperty = mapping?.properties.find(
            (p) => p.targetTypeElementEntryId === property.id,
        );

        if (!(property.typeElementId || mappedProperty)) {
            return true;
        }
        if (isNestedTypeProperty(property) && mappedProperty) {
            // Skip nested types already on this branch to avoid infinite recursion on cyclic types.
            if (nextVisited.has(property.typeElementId)) {
                continue;
            }
            return hasUnmappedNestedProperty(
                types[property.typeElementId],
                mappedProperty.children,
                types,
                nextVisited,
            );
        }
    }

    return false;
};

const AutoMappingTypeUnMatched = ({
    targetTypeElementId,
    types,
    mapping,
    visitedTypeElementIds,
}: Props) => {
    const targetType = types[targetTypeElementId];
    // Track types already on this branch to avoid infinite recursion on cyclic types.
    const nextVisited = new Set(visitedTypeElementIds).add(targetTypeElementId);

    return (
        <ol key={targetType.id}>
            {targetType.properties.sort(sortPropertyByTypeElementDeveloperName).map((property) => {
                const mappedProperty = mapping?.properties.find(
                    (p) => p.targetTypeElementEntryId === property.id,
                );

                if (mappedProperty && !hasUnmappedNestedProperty(targetType, mapping, types)) {
                    return null;
                }

                return (
                    <li key={property.id}>
                        {!mappedProperty || property.typeElementId ? (
                            <PropertyDetails
                                property={property}
                                types={types}
                                isMatched={false}
                                showMatch={false}
                            />
                        ) : null}
                        {isNestedTypeProperty(property) &&
                        !nextVisited.has(property.typeElementId) ? (
                            <AutoMappingTypeUnMatched
                                targetTypeElementId={property.typeElementId}
                                types={types}
                                mapping={mappedProperty?.children}
                                visitedTypeElementIds={nextVisited}
                            />
                        ) : null}
                    </li>
                );
            })}
        </ol>
    );
};

export default AutoMappingTypeUnMatched;
